// v3.15-1089 — Reusable "Save As Template" dialog used by the 4 email
// modals (SendEmailModal, EmailComposeDialog, EditableStageEmailModal,
// SendOfferToAgentModal). Always writes into `email_templates_v2` via the
// legacy POST /email/templates shim — single source of truth.
//
// Props:
//   open           — controlled boolean
//   onOpenChange   — setter
//   defaultSubject — string (current draft subject)
//   defaultBody    — string (current draft body / topMessage)
//   onSaved        — callback({ id, key, name, category }) after success
//
// Admin-only (gated by AuthContext.isAdmin()); the host modal already
// checks this before rendering the trigger button.

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const CATEGORY_OPTIONS = [
  { value: 'client_communication', label: 'Client Communication' },
  { value: 'funding',              label: 'Funding' },
  { value: 'risk',                 label: 'Risk' },
  { value: 'internal',             label: 'Internal' },
  { value: 'custom',               label: 'Custom' },
];

export const SaveAsTemplateDialog = ({
  open,
  onOpenChange,
  defaultSubject = '',
  defaultBody    = '',
  onSaved,
}) => {
  const [name, setName]         = useState('');
  const [category, setCategory] = useState('custom');
  const [subject, setSubject]   = useState('');
  const [body, setBody]         = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setCategory('custom');
    setSubject(defaultSubject || '');
    setBody(defaultBody || '');
  }, [open, defaultSubject, defaultBody]);

  const handleSave = async () => {
    if (!name.trim())     { toast.error('Template name is required'); return; }
    if (!subject.trim())  { toast.error('Subject is required'); return; }
    if (!body.trim())     { toast.error('Body is required'); return; }
    setSaving(true);
    try {
      // POST /email/templates writes into email_templates_v2 (v3.15-1089 shim).
      const { data } = await api.post('/email/templates', {
        template_name: name.trim(),
        subject,
        body,
        category,
      });
      const t = data?.template;
      toast.success(`Template "${t?.name || name}" saved`);
      onSaved?.({ id: t?.id, key: t?.key, name: t?.name, category: t?.category });
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="save-as-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Save className="h-4 w-4" /> Save As Template
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <p className="text-[11px] text-slate-500 leading-snug">
            Template will be added to the universal registry. Available to all
            users via the template selector immediately.
          </p>
          <div>
            <Label className="text-xs text-slate-600">Template name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Welcome Email — Spring Promo"
              className="h-9 text-sm"
              data-testid="save-as-template-name"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 text-sm" data-testid="save-as-template-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`save-as-template-category-${c.value}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Subject *</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="h-9 text-sm"
              data-testid="save-as-template-subject"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Body *</Label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full text-xs font-mono border border-slate-200 rounded p-2 min-h-[160px]"
              data-testid="save-as-template-body"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Use {'{client_name}'}, {'{sender_name}'}, etc. — variables resolve at send time.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving} data-testid="save-as-template-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-as-template-submit">
            {saving ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Saving…</> : 'Save Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveAsTemplateDialog;
